import { useEffect } from "react";

import { getChatApi } from "@/chatApi";
import { hasFloatingMount } from "@/instances";
import { chatLogPrefix } from "@/namespace";

interface UseTomisChatApiParams {
  embed: boolean;
  openWidget: () => void;
  closeWidget: () => void;
  setHidden: (hidden: boolean) => void;
  setProactiveMessages: React.Dispatch<React.SetStateAction<string[]>>;
  onReady: () => void;
}

/**
 * Custom hook to set up the chat API interface
 * Configures different behaviors for embed vs regular widget modes
 */
export const useTomisChatApi = ({
  embed,
  openWidget,
  closeWidget,
  setHidden,
  setProactiveMessages,
  onReady,
}: UseTomisChatApiParams) => {
  useEffect(() => {
    const chat = getChatApi().chat;

    if (embed) {
      // Window API always targets the floating instance. Do not overwrite
      // floating handlers if that mount is already reserved.
      if (!hasFloatingMount()) {
        chat.open = () =>
          console.warn(
            `${chatLogPrefix()} Cannot 'open' chat widget in embed mode`,
          );
        chat.close = () =>
          console.warn(
            `${chatLogPrefix()} Cannot 'close' chat widget in embed mode`,
          );
        chat.hide = () =>
          console.warn(
            `${chatLogPrefix()} Cannot 'hide' chat widget in embed mode`,
          );
        chat.show = () =>
          console.warn(
            `${chatLogPrefix()} Cannot 'show' chat widget in embed mode`,
          );
        chat.proactive = {
          clear: () => setProactiveMessages([]),
          push: () =>
            console.warn(
              `${chatLogPrefix()} Cannot use 'proactive' messages in embed mode`,
            ),
        };
      }
    } else {
      // regular chat widget
      chat.open = openWidget;
      chat.close = closeWidget;
      chat.hide = () => setHidden(true);
      chat.show = () => setHidden(false);
      chat.proactive = {
        clear: () => setProactiveMessages([]),
        push: (...messages: any[]) =>
          setProactiveMessages((prev) => [...prev, ...messages]),
      };
    }

    // This will resolve `createWidget` promise
    onReady();
  }, [
    openWidget,
    closeWidget,
    onReady,
    embed,
    setHidden,
    setProactiveMessages,
  ]);
};
